// Tool registry — single source of truth for the command palette,
// sidebar nav, and hub-page tile lists.
//
// GROUPING AXIS: cattle data domain (Feeder Market, Fed Cattle,
// Boxed Beef, Slaughter, Drought, Cattle Cycle, etc.). Reorganized
// Session 53 to mirror the dominant industry pattern (USDA AMS /
// CME DLR / DTN / Drovers). Audience segments (cow-calf, stocker,
// feedlot, risk manager) are still tracked on each section's
// `audience` field for hub-page copy and analytics, but the primary
// nav axis is data domain — that's what users navigate by when they
// open the app with a specific market question in mind.
//
// To add a tool: put it in the TOOLS array below with the right
// section, a user-facing description, and any keywords you want
// fuzzy search to hit beyond the name itself.

export type ToolSectionId =
  | "dashboard"
  | "feeder-market"
  | "fed-cattle"
  | "boxed-beef"
  | "wasde"
  | "cull-bulls"
  | "cow-calf"
  | "slaughter"
  | "drought-forage"
  | "cattle-cycle"
  | "my-operation"
  | "insights"
  | "admin"
  | "system-admin";

export interface ToolSection {
  id: ToolSectionId;
  label: string;
  /** One-line pitch shown in the sidebar / hub page header. */
  blurb: string;
  /** Primary audience — used for hub page copy and analytics. */
  audience: string;
  /** Display order in the sidebar (1 is top). */
  order: number;
  /**
   * Landing route for this section. Clicking the sidebar section
   * header navigates here. Null for sections that don't need a hub
   * (Dashboard is itself the dashboard; Admin is power-user only).
   */
  hubPath: string | null;
}

export interface Tool {
  id: string;
  name: string;
  path: string;
  section: ToolSectionId;
  /**
   * When true, the tool is hidden from the sidebar for any user that
   * is NOT in `public.user_roles` with `role='admin'`. Used for the
   * `system-admin` section's tools (sale classification, spotlights,
   * sponsor management, data health, etc.) that operate on platform-
   * wide configuration and shouldn't be visible to regular users.
   */
  adminOnly?: boolean;
  /** What producer decision does this tool answer? 1-line, plain English. */
  description: string;
  /**
   * 3-4 short bullets (~4-8 words each) describing what the tool
   * actually renders — sections, widgets, charts, tables. Shown on
   * hub-page tiles as a "What's on this page" preview so the tile
   * works as a scanner instead of a one-line label.
   */
  highlights?: string[];
  /** Extra search terms the name alone might miss. */
  keywords?: string[];
}

export const TOOL_SECTIONS: ToolSection[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    blurb: "Your pinned widgets and quick market read",
    audience: "All",
    order: 1,
    hubPath: null,
  },
  {
    id: "feeder-market",
    label: "Feeder Market",
    blurb: "Live auction prices, weight-break math, and per-barn detail — calves through 800 lb feeders",
    audience: "Cow-calf operator, stocker, backgrounder, feeder buyer, order buyer",
    order: 2,
    // Hub kept at /feeders for URL stability; replaces the old Calves
    // section (Calf Tracker now lives under Feeder Market — same
    // continuum from cow-calf through pre-feedlot weight).
    hubPath: "/feeders",
  },
  {
    id: "fed-cattle",
    label: "Fed Cattle",
    blurb: "Cash-trade history, packer leverage, and regional spread analysis for finished cattle",
    audience: "Feedlot operator, risk manager",
    order: 3,
    hubPath: "/fed-cattle",
  },
  {
    id: "boxed-beef",
    label: "Boxed Beef",
    blurb: "Cutout values, primal detail, and grade-premium spreads — the wholesale beef market",
    audience: "Risk manager, packer-watcher, anyone tracking the cutout-to-cash relationship",
    order: 4,
    hubPath: "/boxed-beef",
  },
  {
    id: "cull-bulls",
    label: "Cull & Bulls",
    blurb: "Slaughter cow + bull market — the cull side of the cow-calf decision",
    audience: "Cow-calf operator, dairy cull, bull-stud sellers",
    order: 5,
    // Phase 2 (Session 53): /cull-bulls hub renders a real recent-
    // sales barn list (top 30 barns by cow + bull head over the
    // last 30 days) since the section has 0 registry-listable tools
    // — the per-barn cow & bull view lives at /barn/:slug/preview.
    hubPath: "/cull-bulls",
  },
  {
    id: "cow-calf",
    label: "Cow-Calf",
    blurb: "Cost of production, weaned calf revenue, replacement-cow and cull markets — the cow-calf economic picture",
    audience: "Cow-calf operator, herd manager, rancher",
    order: 55,
    hubPath: "/cow-calf",
  },
  {
    id: "slaughter",
    label: "Slaughter & Plant",
    blurb: "Plant-level slaughter pace and the daily market-pulse verdict across the beef complex",
    audience: "All (macro context for every segment)",
    order: 6,
    hubPath: "/slaughter",
  },
  {
    id: "drought-forage",
    label: "Drought & Forage",
    blurb: "USDM drought maps, per-barn drought pressure, and pasture-stress tiers — the forage side of supply",
    audience: "All (cow-calf especially)",
    order: 7,
    // Existing /tools/drought page acts as the hub for now; Phase 2
    // may build a dedicated /drought hub if the page count grows.
    hubPath: "/tools/drought",
  },
  {
    id: "cattle-cycle",
    label: "Cattle Cycle",
    blurb: "Inventory, placements, supply-demand balance — the macro cattle herd picture",
    audience: "Risk manager, futures-aware producers",
    order: 8,
    hubPath: "/cattle-cycle",
  },
  {
    id: "wasde",
    label: "WASDE",
    blurb: "USDA's monthly supply-and-demand forecasts — the only forward prices USDA publishes",
    audience: "Feedlot operator, backgrounder, risk manager",
    order: 9,
    // ⚠️ GROUPED BY SOURCE, NOT DOMAIN — a deliberate exception to this file's
    // axis, and the only one. Every other section is a cattle data domain. WASDE
    // is one monthly document that spans several: the fed steer price forecast,
    // the corn balance sheet that sets cost of gain, and beef supply and use.
    // Filing it under Fed Cattle would hide the feed and demand halves; splitting
    // it across three sections would hide that they arrive together, on one day,
    // and revise together. The industry already treats "the WASDE" as an event.
    hubPath: "/tools/wasde-this-month",
  },
  {
    id: "my-operation",
    label: "My Operation",
    blurb: "Breakeven math, trucking cost, and alerts for your operation",
    audience: "All",
    order: 10,
    hubPath: "/my-operation",
  },
  {
    id: "insights",
    label: "Insights & AI",
    blurb: "Cattle Consultant, daily briefs, weekly digests, news, and custom reports",
    audience: "All",
    order: 11,
    hubPath: "/insights",
  },
  {
    id: "admin",
    label: "Data & Settings",
    blurb: "Data pipeline status, schema reference, and account settings",
    audience: "Power user / admin",
    order: 12,
    hubPath: null,
  },
  {
    id: "system-admin",
    label: "Admin",
    blurb: "Platform configuration — only visible to admins",
    audience: "System admin",
    order: 13,
    hubPath: null,
  },
];

export const TOOLS: Tool[] = [
  // ── DASHBOARD ──────────────────────────────────────────────────
  {
    id: "dashboard",
    name: "Dashboard",
    path: "/dashboard",
    section: "dashboard",
    description: "Your pinned widgets and quick market read",
    keywords: ["home"],
  },

  // ── FEEDER MARKET (incl. calves — same supply-chain continuum) ─
  {
    id: "calf-tracker",
    name: "Calf Tracker",
    path: "/calf-tracker",
    section: "feeder-market",
    description: "Calf-weight cohort prices across the auction footprint",
    highlights: [
      "Top 3wt and 4wt calf sales by $/cwt",
      "Ranked sales with barn, state, head, weight",
      "Most recent mixed-sex sales panel",
      "Market read narrative",
    ],
    keywords: ["calf", "weaning", "cow-calf", "300", "400"],
  },

  {
    id: "regional-markets",
    name: "Regional Markets",
    path: "/regional-markets",
    section: "feeder-market",
    description: "Live feeder cattle auction prices by region and weight",
    highlights: [
      "US map with regional price visualization",
      "Best-sale scanner across auction markets",
      "State rankings vs national average",
      "DV prices by state comparison",
    ],
    keywords: ["auction", "VWAP", "feeder", "regional", "plains", "cornbelt"],
  },
  {
    id: "dv-auction",
    name: "DV Auction Reports",
    path: "/dv-auction",
    section: "feeder-market",
    description: "Sale-barn deep dive — lot-level data from DV Auction",
    highlights: [
      "Steers vs heifers price comparison",
      "Volume scorecard + receipt mix",
      "Weight slide calculator and analyzer",
      "Barn report cards + 52-week highs",
    ],
    keywords: ["sale barn", "lot", "DV", "dvauction", "feeder"],
  },
  {
    id: "market-report",
    name: "Weekly Steer Prices",
    path: "/market-report",
    section: "feeder-market",
    description: "This week's auction summary by weight class",
    highlights: [
      "National steer prices weekly table",
      "Price change by weight class + trend icons",
      "52-week high tracking per weight class",
      "AI-generated market commentary panel",
    ],
    keywords: ["weekly", "summary", "feeder"],
  },
  {
    id: "live-market-board",
    name: "Live Market Board",
    path: "/live",
    section: "feeder-market",
    description: "Comprehensive live market overview — tickers, feeder cohort grid, pulse, price spreads, cattle + boxed beef reports",
    highlights: [
      "National Beef Wire tickers — indices + CME futures",
      "Feeder cattle slide — 3 regions × 5 weights",
      "Market Pulse + National Price Spread snapshots",
      "USDA cattle report + boxed beef report panels",
    ],
    keywords: ["live", "market", "board", "dashboard", "overview", "tickers", "cohort", "feeder", "home"],
  },
  {
    id: "national-steer-tracker",
    name: "National Steer Tracker",
    path: "/tools/feeders/national-steer-tracker",
    section: "feeder-market",
    description: "Live pulse of today's 700-800 wt steer market across every active sale barn",
    highlights: [
      "Live ticker of latest qualifying prints",
      "National head-weighted W/A for 700s + 800s",
      "Per-barn card grid with last 7-wt + 8-wt sale",
      "Amber flash when a new sale lands at a barn",
    ],
    keywords: ["live", "steer", "tracker", "auction", "feeder", "700", "800", "ticker", "realtime", "national", "pulse"],
  },
  {
    id: "state-feeder-direct",
    name: "State Feeder Direct & Forward Curve",
    path: "/tools/feeders/state-direct",
    section: "feeder-market",
    description: "14-state direct trade heatmap + forward curve nobody else publishes weekly",
    highlights: [
      "14-state × 5-cohort weighted-avg spot heatmap",
      "Forward curve by delivery month (Jun/Jul/Aug/Sep/Oct)",
      "Spot vs Forward spread per state — contango / backwardation signal",
      "Direct trade (not auction) — separate price discovery channel",
      "5.6 years of LM Direct Cattle history",
    ],
    keywords: ["state", "direct", "feeder", "forward", "curve", "spread", "contango", "backwardation", "LM direct cattle", "Texas", "Kansas", "Oklahoma"],
  },
  {
    id: "steer-tracker-daily",
    name: "Steer Tracker — Daily",
    path: "/tools/feeders/steer-tracker-daily",
    section: "feeder-market",
    description: "Daily summary of feeder steer prices by 100-lb weight bucket — filterable by barn, state, region",
    highlights: [
      "KPI strip: Steers 700-899 + Calves 400-499 W/A",
      "Section 1: all 8 weight buckets — head, low/high, $/head, W/A, slide",
      "Section 2A: 5-day history across every band",
      "Filter by barn, state, or sourcing region",
    ],
    keywords: ["daily", "summary", "steer", "calves", "weight", "bucket", "slide", "history", "report", "feeder", "auction"],
  },
  {
    id: "clara-live-tracker",
    name: "Live Feeder Prices by State",
    path: "/tools/feeders/clara-live-tracker",
    section: "feeder-market",
    description: "Live pulse of today's feeder market by state — pick any class and weight",
    highlights: [
      "Class toggle (Steers / Heifers) + weight chips (300s–900s)",
      "State cards: last lot, state W/A, delta vs national",
      "Live ticker + amber flash on each new print",
      "Click a state to see every qualifying lot today",
    ],
    keywords: ["live", "clara", "tracker", "feeder", "state", "auction", "heifer", "steer", "realtime", "pulse", "ticker", "weight"],
  },

  // ── FED CATTLE ─────────────────────────────────────────────────
  {
    id: "packer-leverage",
    name: "Cash Margin & Leverage Gauge",
    path: "/tools/packer-leverage",
    section: "fed-cattle",
    description: "Who has leverage in this week's cash-trade negotiation",
    highlights: [
      "Producer leverage gauge + hero verdict",
      "KPI strip: gross margin, net, percentile",
      "Cutout vs cash dual-line chart",
      "Spread with seasonal p25/p50/p75 band",
    ],
    keywords: ["packer", "leverage", "supply-demand"],
  },
  {
    id: "packer-posture",
    name: "Boxed Beef Cutout & Primals",
    path: "/tools/packer-posture",
    // Moved to boxed-beef Session 53 — the page is fundamentally a
    // boxed-beef cutout + primal detail surface ("Boxed beef cutout +
    // primal detail" per its own highlights). Keeping it under
    // fed-cattle hid the boxed-beef story behind a fed-cash label.
    section: "boxed-beef",
    description: "Which plants are in position to buy cattle this week",
    highlights: [
      "Boxed beef cutout + primal detail",
      "5-day trend for each primal cut",
      "AI-generated supply-chain narrative",
    ],
    keywords: ["packer", "posture", "plants"],
  },
  {
    id: "wasde-this-month",
    name: "This Month's WASDE",
    path: "/tools/wasde-this-month",
    section: "wasde",
    description: "What changed in this month's report?",
    highlights: [
      "Fed cattle, corn and beef supply at a glance",
      "Every figure against what USDA said last month",
      "What moved most, ranked",
      "Pork, chicken, turkey, eggs and milk for context",
    ],
    keywords: ["wasde", "this month", "summary", "changes", "report", "at a glance", "hub"],
  },
  {
    id: "usda-beef-supply",
    name: "USDA Beef Supply",
    path: "/tools/usda-beef-supply",
    section: "wasde",
    description: "How much beef is coming, and did USDA change its mind?",
    highlights: [
      "Beef production by quarter, with last month's figure",
      "Supply and use: imports, exports, per person",
      "Production and consumption over 19 years",
      "Both production bases, each labelled",
    ],
    keywords: ["beef", "production", "supply", "wasde", "exports", "imports", "per capita"],
  },
  {
    id: "usda-fed-cattle-forecast",
    name: "USDA Fed Cattle Forecast",
    path: "/tools/usda-fed-cattle-forecast",
    section: "wasde",
    description: "Did USDA raise or cut its fed cattle price outlook this month?",
    highlights: [
      "5-area direct steer price by quarter, out four quarters",
      "Revision vs last month's release, per quarter",
      "Corn season-average price beside it",
      "Revision translated to $/head at USDA's reported live weight",
    ],
    keywords: ["wasde", "forecast", "waob", "outlook", "projection", "steers", "corn", "revision"],
  },
  {
    id: "usda-forecast-path",
    name: "USDA Forecast Path",
    path: "/tools/usda-forecast-path",
    section: "wasde",
    description: "Is USDA drifting or reversing on the quarter I'm selling into?",
    highlights: [
      "USDA's forecast for one quarter, release by release",
      "Any quarter back to 2010 — 34 to choose from",
      "Settled quarters show the actual and USDA's miss",
      "Drift, range and month-on-month change",
    ],
    keywords: ["wasde", "path", "revision", "history", "drift", "track", "forecast", "waob"],
  },
  {
    id: "usda-track-record",
    name: "USDA Track Record",
    path: "/tools/usda-track-record",
    section: "wasde",
    description: "How much should I trust USDA's forecast in the first place?",
    highlights: [
      "USDA graded against what the market actually did",
      "How far off, by how far ahead the forecast was",
      "Which way they miss — and how often",
      "Every graded quarter back to 2011",
    ],
    keywords: ["wasde", "accuracy", "track record", "reliability", "error", "trust", "graded"],
  },
  {
    id: "usda-corn-outlook",
    name: "USDA Corn Outlook",
    path: "/tools/usda-corn-outlook",
    section: "wasde",
    description: "Is feed getting dearer or cheaper, and what does that do to my calf price?",
    highlights: [
      "Corn price and why USDA moved it",
      "How much is left in the bin, 19 years of it",
      "The full balance sheet, this month vs last",
      "Translated to feed cost per head",
    ],
    keywords: ["corn", "wasde", "feed", "cost of gain", "stocks", "bushel", "ration", "grain"],
  },
  {
    id: "choice-daily-cutout",
    name: "Choice Daily Cutout Values",
    path: "/tools/choice-daily-cutout",
    section: "boxed-beef",
    description: "The month's Choice cutout tape as a published report, ready to export as a PDF",
    highlights: [
      "Month-to-date Choice cutout, measured from the prior month's close",
      "Daily tape with the month's high and biggest gain marked",
      "Rally, giveback and month change computed from USDA's own prints",
      "One-click PDF of the same document",
    ],
    keywords: ["cutout", "boxed beef", "choice", "report", "pdf"],
  },
  {
    id: "primal-seasonality",
    name: "Primal Seasonality",
    path: "/tools/primal-seasonality",
    section: "boxed-beef",
    description: "What each primal averages — and which month it typically peaks — across 25 years of USDA data",
    highlights: [
      "Trailing 12-mo / YTD / 5-yr averages per primal",
      "Month-by-month seasonal curve with typical range",
      "Peak-month frequency (which month is the year's high)",
      "Filter by primal, Choice/Select, and year range",
    ],
    keywords: ["rib", "loin", "chuck", "seasonal", "monthly average", "primal"],
  },
  {
    id: "holiday-cut-tracker",
    name: "Holiday Cut Tracker",
    path: "/tools/holiday-cut-tracker",
    section: "boxed-beef",
    description: "Each holiday's signature primal run-up — and whether this year is ahead of or behind the typical curve",
    highlights: [
      "5 beef holidays with countdowns",
      "Typical 12-week run-up curve vs this year",
      "Pace gauge: running hot, on pace, or behind",
      "~22 years of history per holiday",
    ],
    keywords: ["thanksgiving", "christmas", "rib", "brisket", "grilling", "holiday", "seasonal"],
  },
  {
    id: "steak-burger-gauge",
    name: "Steak/Burger Gauge",
    path: "/tools/steak-burger-gauge",
    section: "boxed-beef",
    description: "Are consumers trading up to steaks or down to ground? Middle meats vs end meats as a demand-quality read",
    highlights: [
      "Demand-quality gauge with 5-yr percentile",
      "Middles÷ends ratio history to 2004",
      "Steak vs burger complex price lines",
      "Week-over-week and year-over-year shifts",
    ],
    keywords: ["middle meats", "end meats", "demand", "consumer", "ground beef", "steak", "ratio"],
  },
  {
    id: "barn-vs-packer-paid",
    name: "Barn vs Packer-Paid",
    path: "/barn-vs-packer",
    section: "fed-cattle",
    description: "Which sale barns clear above what packers pay direct — and which run under",
    highlights: [
      "Every fat cattle barn ranked by its spread to negotiated cash",
      "Weekly trend per barn against a zero line",
      "Swing shown beside the average, so a big spread with big scatter reads as noise",
      "Current week flagged and excluded from the average",
    ],
    keywords: [
      "barn", "packer", "spread", "basis", "where to haul", "premium", "discount",
      "negotiated cash", "fat cattle", "auction vs direct",
    ],
  },
  {
    id: "fat-cattle-sale-results",
    name: "Fat Cattle Sale Results",
    path: "/fat-cattle-sales",
    section: "fed-cattle",
    description: "Pick a barn and a date — see what the fat cattle brought, by weight",
    highlights: [
      "Two dropdowns: sale barn, sale date",
      "Head, average weight, price and range per weight class",
      "Steers and heifers separate",
      "Every class that sold, including small ones",
    ],
    keywords: [
      "sale results", "fat cattle", "sale barn", "auction", "lookup",
      "weight class", "recap", "what did they bring",
    ],
  },
  {
    id: "fed-cattle-auction-barometer",
    name: "Fed Cattle Auction Barometer",
    path: "/fed-cattle-auction",
    section: "fed-cattle",
    description: "What finished cattle bring at open auction — the public half of the fed market",
    highlights: [
      "Auction VWAP vs USDA negotiated cash, and whether the gap is closing",
      "What another pound is worth: $/head by weight, per sex",
      "Steers show no ceiling; heifers peak near 1,570 lb",
      "Weight-class filter, per-barn results, and the full price tape",
    ],
    keywords: [
      "auction", "fat cattle", "fed cattle", "barometer", "yankton",
      "stockmen's", "sale barn", "price discovery", "ship weight", "cost of gain",
    ],
  },
  {
    id: "recent-trade-recap",
    name: "Recent Trade Recap",
    path: "/tools/fed-cattle/recent-trades",
    section: "fed-cattle",
    description: "What packers paid in this week's cash trade",
    highlights: [
      "Last 7 days USDA negotiated cash trades",
      "Live basis (Plains) + dressed (Iowa-MN)",
      "Region rows with daily prices + 7-day avg",
      "Head count + weighted average weight",
    ],
    keywords: ["cash", "trade", "LM_CT100", "recent"],
  },
  {
    id: "day-of-week",
    name: "Fed Cash by Day of Week",
    path: "/tools/fed-cattle/day-of-week",
    section: "fed-cattle",
    description: "Best day of the week to sell fed cattle historically",
    highlights: [
      "2-year weekday average price bar chart",
      "Five bars (Mon–Fri) with $/cwt labels",
      "Sample size + head-weighted average per day",
      "Region selector: National, Plains, IA-MN",
    ],
    keywords: ["timing", "cash", "day", "dow"],
  },
  {
    id: "seasonal-setup",
    name: "Seasonal Cash Trend",
    path: "/tools/fed-cattle/seasonal-setup",
    section: "fed-cattle",
    description: "Is the seasonal wind at your back in the cash market?",
    highlights: [
      "TAILWIND / NEUTRAL / HEADWIND verdict",
      "Seasonal deviation band (p25/p50/p75)",
      "8-year history with current overlay",
      "Forward 4-week historical outlook",
    ],
    keywords: ["seasonal", "tailwind", "headwind", "fedcc"],
  },
  {
    id: "regional-pulse",
    name: "Regional Cash Spreads",
    path: "/tools/fed-cattle/regional-pulse",
    section: "fed-cattle",
    description: "Does my region have leverage in the cash trade this week?",
    highlights: [
      "4 regions: TX-OK-NM, KS, NE, IA-MN",
      "Spread vs 5-Area weekly average",
      "EDGE / BALANCED / TIGHT verdict per region",
      "12-week trend sparkline + posture box",
    ],
    keywords: ["regional", "basis", "spread", "CT117", "CT120", "CT123", "CT136"],
  },
  {
    id: "captive-supply",
    name: "Captive Supply Tracker",
    path: "/tools/fed-cattle/captive-supply",
    section: "fed-cattle",
    description: "How much of next week's kill is already pre-bought — the producer-leverage signal",
    highlights: [
      "Self-calibrating 5-yr p25/p75 verdict — PRODUCER LEVERAGE HIGH/LOW/NEUTRAL",
      "9.6 yrs of weekly captive % history with reference lines",
      "AI Captive Supply Read — envelope-grounded Claude Opus 4.7 prose",
      "Regional volume mix (this-week head count by region + WoW)",
      "USDA LM_CT142, the Cattle Market Transparency Act centerpiece signal",
    ],
    keywords: ["captive", "supply", "formula", "forward", "contract", "leverage", "transparency act", "LM_CT142"],
  },
  {
    id: "feedlot-leverage-index",
    name: "Feedlot Leverage Index",
    path: "/tools/fed-cattle/leverage-index",
    section: "fed-cattle",
    description: "5-Area Live Steer ÷ Choice Cutout — who's gaining bargaining power week to week",
    highlights: [
      "Daily LM_CT100 fed cash vs LM_XB403 PM Choice cutout",
      "Ratio rising = feedlot leverage; falling = packer leverage",
      "30d / 90d / 1Y / 5Y window dropdown",
      "Latest ratio + day-over-day change in the header",
    ],
    keywords: ["leverage", "ratio", "cutout", "cash", "fed steer", "packer", "feedlot", "index", "boxed beef"],
  },
  {
    id: "cash-vs-grid-spread",
    name: "Cash vs Grid Premium Spread",
    path: "/tools/fed-cattle/cash-vs-grid-spread",
    section: "fed-cattle",
    description: "Weekly cash − grid_base spread — packer-urgency signal nobody else publishes",
    highlights: [
      "USDA LM_CT154 weekly headline cash vs grid base price",
      "LIVE FOB + DRESSED DELIVERED both shown",
      "Verdict: cash >= +$2 above grid = producer leverage; <= -$0.50 = packer leverage",
      "52-week chart with verdict-threshold reference lines",
      "9.6 years of history available (2016-10-24 onward)",
    ],
    keywords: ["spread", "cash", "grid", "premium", "leverage", "packer", "LM_CT154", "negotiated", "weekly"],
  },
  {
    id: "price-distribution",
    name: "Same-Week Price Distribution",
    path: "/tools/fed-cattle/price-distribution",
    section: "fed-cattle",
    description: "Where your price ranked vs every other producer who sold the same basis that week",
    highlights: [
      "USDA LM_CT150 per-\\$0.50/cwt head_count histogram",
      "Enter your realized price → percentile rank + head above/below",
      "4 selling bases (Live FOB / Live Delivered / Dressed FOB / Dressed Delivered)",
      "Verdict pill: top quartile / above median / below median / bottom quartile",
    ],
    keywords: ["distribution", "percentile", "rank", "histogram", "audit", "LM_CT150", "cash", "negotiated"],
  },
  {
    id: "cash-seasonality-calendar",
    name: "Cash Trade Seasonality Calendar",
    path: "/tools/fed-cattle/cash-seasonality",
    section: "fed-cattle",
    description: "9.6-year heatmap + multi-year overlay — answers 'is now a typical time for cash to rally?'",
    highlights: [
      "Current-week KPI: this week historically rallies in X of Y years",
      "Heatmap of WoW % change across 10 yrs × 52 weeks",
      "Multi-year line overlay (current year highlighted)",
      "USDA LM_CT154 LIVE FOB negotiated cash, back to 2016",
    ],
    keywords: ["seasonality", "calendar", "heatmap", "seasonal", "pattern", "LM_CT154", "historical", "rally", "WoW"],
  },
  {
    id: "choice-select",
    name: "Choice/Select Spread",
    path: "/tools/fed-cattle/choice-select",
    // Moved to boxed-beef Session 53 — Choice/Select is a boxed-beef
    // grade-premium spread, not a fed-cash decision tool. URL kept
    // stable for bookmark + acquirer-diligence reasons.
    section: "boxed-beef",
    description: "Grade premium between Choice and Select boxed beef",
    highlights: [
      "Current Choice-Select spread snapshot",
      "Daily Choice + Select cutout line chart",
      "Spread vs seasonal p25/p50/p75 band",
      "30/90/1y range toggle",
    ],
    keywords: ["spread", "boxed beef", "cutout", "choice", "select"],
  },

  // ── SLAUGHTER & PLANT ─────────────────────────────────────────
  // (Plant Pace retired Session 60 — its Seasonal Kill Range chart was
  //  merged into MarketPulse's Kill Pace Monitor section; the other two
  //  charts were redundant with Market Pulse's existing kill-pace views.)
  {
    id: "supply-demand",
    name: "Supply & Demand",
    path: "/supply-demand",
    section: "cattle-cycle",
    description: "National cattle supply-demand balance overview",
    highlights: [
      "Weight-class demand heatmap + momentum",
      "Price trend (top 3 weight classes, 90d)",
      "Weekly receipts vs avg price chart",
      "Regional supply index by state",
    ],
    keywords: ["supply", "demand", "balance"],
  },
  {
    id: "cattle-on-feed",
    name: "Cattle on Feed",
    path: "/cattle-on-feed",
    section: "cattle-cycle",
    description: "Feedlot inventory and placement trend (USDA COF)",
    highlights: [
      "Monthly inventory trend (all classes)",
      "Placements stacked by weight class",
      "Year-over-year inventory comparison",
      "Seasonal pattern with percentile bands",
    ],
    keywords: ["placements", "on feed", "COF", "USDA"],
  },
  {
    id: "cattle-inventory",
    name: "Cattle Inventory",
    path: "/tools/cattle-inventory",
    section: "cattle-cycle",
    description: "USDA Quarterly Cattle Inventory — whole US herd + cycle position",
    highlights: [
      "Total US cattle + 7 class breakdowns (semi-annual)",
      "Replacement Heifer / Beef Cow ratio with 5-yr MA",
      "Herd cycle verdict (expansion / neutral / contraction)",
      "20-year cycle history chart + top-5 beef cow states",
    ],
    keywords: ["inventory", "beef cows", "herd cycle", "replacement heifers", "rebuilding", "liquidating", "USDA", "NASS"],
  },
  {
    id: "market-pulse",
    name: "Daily Market Verdict Score",
    path: "/market-pulse",
    section: "slaughter",
    description: "National daily verdict score across the beef complex",
    highlights: [
      "Market pulse score card (0–100)",
      "Kill pace: current vs year-ago",
      "Packer margin (cutout minus cash) trend",
      "Net margin seasonal band",
    ],
    keywords: ["pulse", "verdict", "daily", "score"],
  },
  {
    id: "per-head-supply",
    name: "Per-Head Supply Index",
    path: "/tools/per-head-supply",
    section: "slaughter",
    description: "Cattle slaughter head × dressed weight — the real per-head supply read when kill and weights move in opposite directions",
    highlights: [
      "Weekly tonnage from SJ_LS712 with YoY normalized to weekly-equivalent",
      "5-yr seasonal band (p25/p50/p75) from 52 years of NASS history",
      "36-month tonnage chart for cycle-position context",
      "Component decomposition: head count vs dressed weight contributions",
    ],
    keywords: ["per-head", "supply", "tonnage", "carcass", "slaughter", "weight", "kill", "supply pressure"],
  },
  {
    id: "cold-storage",
    name: "Cold Storage — Frozen Beef",
    path: "/tools/cold-storage",
    section: "slaughter",
    description: "Monthly USDA frozen beef inventory — the institutional demand-side signal showing whether packers are sitting on meat (defensive on cash) or running short (need to bid harder)",
    highlights: [
      "BULLISH/BEARISH/NEUTRAL verdict anchored on ±3% vs 5-year seasonal average",
      "Latest month total + boneless + bone-in cut breakdown + YoY",
      "36-month stacked-bar history with 5-yr-average reference line",
      "12-month seasonal band (p25/p50/p75) from prior 5-year window",
      "Aggregate beef series goes back to 1917; cut breakdowns to 1973",
    ],
    keywords: ["cold storage", "frozen", "inventory", "stocks", "demand", "packer posture"],
  },
  {
    id: "cot-positioning",
    name: "CFTC Positioning — Who's Long, Who's Short?",
    path: "/tools/cot-positioning",
    section: "slaughter",
    description: "Weekly hedge-fund vs commercial positioning in Live Cattle, Feeder Cattle, and Corn futures. Contrarian indicator — when speculators crowd one side, the trade is vulnerable to mean reversion.",
    highlights: [
      "BULLISH/BEARISH/NEUTRAL verdict per market via ±80 percentile extremes",
      "Managed Money long/short/net for the 3 markets cattle producers care about",
      "52-week chart of MM net positioning vs commercials (hedger side)",
      "5-year percentile rank flags overcrowded long or short positioning",
      "Updated every Friday 3:30 PM ET for prior-Tuesday positions",
    ],
    keywords: ["cot", "cftc", "speculator", "managed money", "futures", "positioning", "contrarian", "hedge fund"],
  },
  {
    id: "feeding-extension",
    name: "Feeding Extension Indicator",
    path: "/tools/feeding-extension",
    section: "slaughter",
    description: "Are cattle being held longer than the cash market priced for? Cash trade dressed weight (bought) vs slaughter dressed weight (killed) — the spread is producer leverage in real time.",
    highlights: [
      "LSWFEDCC weekly cash trade weight vs SJ_LS712 weekly slaughter weight",
      "26-week side-by-side trend chart (watch the gap)",
      "36-month apples-to-apples (both fed-cattle-only, NASS-derived)",
      "Auto-narrative: gap widening → producer leverage tilting up",
    ],
    keywords: ["feeding extension", "days on feed", "cash trade", "slaughter weight", "carcass", "producer leverage", "fed cattle"],
  },
  {
    // ── DROUGHT & FORAGE (own section Session 53) ─────────────────
    id: "drought-intelligence",
    name: "Drought & Forage",
    path: "/tools/drought",
    section: "drought-forage",
    description: "National + per-state drought map, DSCI trend, and supply-side cattle implications",
    highlights: [
      "Hero national USDM map + rules-based read",
      "Worst week-over-week and top cattle states elevated",
      "2-year national DSCI trend",
      "Sortable 51-state grid → drill into any state",
    ],
    keywords: ["drought", "USDM", "DSCI", "pasture", "forage", "supply", "weather", "monitor"],
  },
  {
    id: "drought-pressure",
    name: "Drought Pressure Index",
    path: "/tools/drought-pressure",
    section: "drought-forage",
    description: "Per-barn drought pressure score combining USDM county drought, receipt surges, and price discounts",
    highlights: [
      "Top-10 watch list ranked by composite pressure score",
      "Per-barn drill-down with 26-week drought sparkline",
      "Sample-size hybrid: county-level read where available, state-fallback otherwise",
      "Acute (lag-0) vs +2-week forward USDM regime toggle",
    ],
    keywords: ["drought", "pressure", "barn", "auction", "receipt surge", "forced movement", "supply"],
  },
  {
    id: "pasture-stress",
    name: "Pasture Stress Index",
    path: "/pasture-stress",
    section: "drought-forage",
    description: "Per-state pasture & range condition tier (Critical / Deteriorating / Watch / Healthy) with momentum and seasonality context",
    highlights: [
      "National narrative read on this week's pasture & range",
      "51-state stress tier classification with week-over-week momentum",
      "Per-state drill-down with multi-year stress history chart",
      "Seasonality-aware percentile against the same week historical band",
    ],
    keywords: ["pasture", "range", "condition", "stress", "forage", "NASS", "drought", "supply", "carrying capacity"],
  },

  // ── COW-CALF ───────────────────────────────────────────────────
  {
    id: "cow-calf-economics",
    name: "Cow-Calf Economics Dashboard",
    path: "/cow-calf/economics",
    section: "cow-calf",
    description: "Cost of production + weaned calf revenue + replacement + cull markets by state or US national",
    highlights: [
      "Cash cow cost + 30-year trend (USDA ERS)",
      "Feeder revenue by weight class (26 states, weekly)",
      "Replacement cattle + cull cow/bull revenue",
      "Drought DSCI + calf-per-cow ratio + Cycle view toggle",
    ],
    keywords: ["cow-calf", "ranch", "weaned calf", "cull cow", "bred heifer", "cost of production", "ERS", "cow"],
  },

  // ── MY OPERATION ───────────────────────────────────────────────
  {
    id: "profitability",
    name: "Profitability",
    path: "/profitability",
    section: "my-operation",
    description: "Breakeven and margin math for your operation",
    highlights: [
      "Should-I-sell decision card + calculator",
      "Basis + spread detail (feeder barometer)",
      "Placement margin indicator with breakeven",
    ],
    keywords: ["margin", "breakeven", "cost"],
  },
  {
    id: "freight",
    name: "Freight",
    path: "/freight",
    section: "my-operation",
    description: "Trucking cost calculator and diesel tracker",
    highlights: [
      "Diesel price banner (current, change, trend)",
      "Cheapest barns by distance",
      "Freight impact calculator ($/cwt output)",
      "Feedlot location manager with map pins",
    ],
    keywords: ["trucking", "diesel", "cost", "freight"],
  },
  {
    id: "barn-price-ranking",
    name: "Barn Price Ranking",
    path: "/tools/barn-price-ranking",
    section: "feeder-market",
    description: "Which barns sell a weight class under the market, and which sell over it",
    highlights: [
      "Every barn ranked for one weight class, cheapest first",
      "Flip it for selling — dearest first",
      "How often each barn lands on that side of the market",
      "Measured against the market on the days that barn actually sold",
    ],
    keywords: [
      "cheapest", "highest", "lowest", "where to buy", "where to sell",
      "barn", "ranking", "premium", "discount", "order buyer", "weight class",
    ],
  },
  {
    id: "value-added-premium",
    name: "Value-Added Premium Tracker",
    path: "/tools/value-added-premium",
    section: "my-operation",
    description: "What the market pays for weaning, preconditioning, and value-added programs",
    highlights: [
      "Weaning discount + value-added premium, like-for-like",
      "Per-program $/cwt and $/head",
      "Sample-gated with confidence flags",
      "Gross of your added cost — you net it out",
    ],
    keywords: ["weaning", "preconditioning", "value added", "premium", "natural", "nhtc", "vac", "cow-calf"],
  },
  {
    id: "value-of-gain",
    name: "Value of Gain & Optimal Sell Weight",
    path: "/tools/value-of-gain",
    section: "my-operation",
    description: "What the market pays for the next pound vs your cost to put it on",
    highlights: [
      "Value-of-gain curve by weight class",
      "Your cost of gain flags where adding weight stops paying",
      "USDA auction price slide (not futures)",
      "Hay-anchored cost-of-gain default you can override",
    ],
    keywords: ["value of gain", "sell weight", "optimal", "stocker", "backgrounder", "gain", "cost of gain"],
  },
  {
    id: "video-forward-market",
    name: "Video & Forward Market",
    path: "/tools/video-forward-market",
    section: "feeder-market",
    description: "What forward-delivery cattle bring on the video auctions — a feeder forward curve",
    highlights: [
      "Forward curve: price by delivery month vs current",
      "Forward board: premium by weight × delivery window",
      "USDA video/internet sales (Superior, Western Video…)",
      "Forward market only — never mixed with spot cash",
    ],
    keywords: ["video", "superior", "forward", "internet auction", "delivery", "forward contract", "fall delivery"],
  },

  // ── INSIGHTS ───────────────────────────────────────────────────
  {
    id: "clara-consultant",
    name: "RaisedBeef Cattle Consultant",
    path: "/consultant",
    section: "insights",
    description: "AI cattle consultant with live DV Auction data + USDA fed-cattle + boxed beef tools",
    highlights: [
      "Conversation history — pick up where you left off",
      "Suggested prompts tailored to your watchlist",
      "Pulls live market data as part of every answer",
      "Inline charts + tables rendered in the chat",
    ],
    keywords: ["clara", "consultant", "ask", "ai", "chat", "assistant", "gpt"],
  },
  {
    id: "test-an-assumption",
    name: "Test an Assumption",
    path: "/tools/test-an-assumption",
    section: "insights",
    description: "Check a belief about how the market works against 25+ years of USDA record — including every time it did not work",
    highlights: [
      "Set the test yourself: what happens, what follows, how long after",
      "Five checks before anything is called a pattern, each shown with its numbers",
      "Every occasion the rule broke, listed and never hidden",
      "Says plainly when the record is too thin to judge, instead of guessing",
    ],
    keywords: ["assumption", "test", "hypothesis", "backtest", "base rate", "does it hold", "history", "pattern", "rule"],
  },
  {
    id: "daily-briefs",
    name: "Daily Briefs",
    path: "/briefs",
    section: "insights",
    description: "AM + PM daily briefs across Stocker, Sale-Day, Feedyard, and General",
    highlights: [
      "4 brief types tuned to each cattle-supply-chain audience",
      "AM forward-look + PM recap, every weekday",
      "Subscribe in Settings to get briefs in email",
      "Top anomalies highlighted per brief",
    ],
    keywords: ["brief", "daily", "AM", "PM", "stocker", "saleday", "feedyard", "AI", "summary"],
  },
  {
    id: "market-calendar",
    name: "Market Report Calendar",
    path: "/market-calendar",
    section: "insights",
    description: "When the market-moving USDA reports drop, what they mean, and where to read them",
    highlights: [
      "Countdown to Cattle on Feed, COT, Cold Storage, Drought Monitor…",
      "Plain-English 'why it matters' + link to the tool that shows it",
      "USDA-published dates; unconfirmed ones labeled 'estimated'",
      "Headline-only toggle + recently-released history",
    ],
    keywords: ["calendar", "report", "schedule", "USDA", "NASS", "cattle on feed", "COT", "cold storage", "upcoming", "release", "economic calendar", "bloomberg"],
  },
  {
    id: "weekly-digests",
    name: "Weekly Digests",
    path: "/news/weekly",
    section: "insights",
    description: "Weekly market summary",
    highlights: [
      "Weekly digest cards with direction badge",
      "Executive summary + trend analysis",
      "Key themes with trajectory labels",
      "Notable shifts + market outlook",
    ],
    keywords: ["weekly", "digest", "summary"],
  },
  {
    id: "report-builder",
    name: "Report Builder",
    path: "/report-builder",
    section: "insights",
    description: "Custom market report generator",
    highlights: [
      "Multi-type report generator (weight/state/date)",
      "Saved presets with load + delete",
      "Scheduled report manager with recurrence",
      "Print, email, download, lock-state actions",
    ],
    keywords: ["report", "export", "custom", "builder"],
  },

  // ── ADMIN ──────────────────────────────────────────────────────
  {
    id: "settings",
    name: "Settings",
    path: "/settings",
    section: "admin",
    description: "Profile, alerts, and watchlist settings",
    highlights: [
      "Operator type + profile",
      "Price alerts by weight class and region",
      "Watchlist management (My Markets)",
    ],
    keywords: ["settings", "profile", "alerts", "watchlist"],
  },
  {
    id: "data-integrations",
    name: "Data Integrations",
    path: "/data-integrations",
    section: "admin",
    description: "USDA and DV Auction pipeline status",
    highlights: [
      "Data source health status table",
      "Live record count + last update per table",
      "Data model schemas with descriptions",
      "Edge function runner + inspectors",
    ],
    keywords: ["USDA", "ingest", "pipeline", "integrations"],
  },
  {
    id: "data-schema",
    name: "Data Schema",
    path: "/data-integrations/schema",
    section: "admin",
    description: "Database schema reference",
    keywords: ["schema", "tables", "database"],
  },
  {
    id: "data-architecture",
    name: "Data Architecture",
    path: "/data-integrations/architecture",
    section: "admin",
    description: "Data pipeline architecture diagram",
    keywords: ["architecture", "diagram"],
  },

  // ── SYSTEM ADMIN (visible only to users with role='admin') ─────
  {
    id: "admin-sales",
    name: "Sales (Classification)",
    path: "/admin/sales",
    section: "system-admin",
    adminOnly: true,
    description: "Manage sale entities — classify, edit, split, merge, expand to view lots",
    highlights: [
      "Week view with sale-type badges per barn × day",
      "Per-row edit, split, merge, expand to inspect every lot",
      "Auto-classification + manual-override discipline",
    ],
    keywords: ["admin", "sales", "classification", "split", "merge", "feeder", "cow", "bull"],
  },
  {
    id: "admin-spotlights",
    name: "Market Spotlights",
    path: "/admin/spotlights",
    section: "system-admin",
    adminOnly: true,
    description: "Curate market spotlight entries that appear on the homepage",
    keywords: ["admin", "spotlights", "homepage"],
  },
  {
    id: "admin-clara-sponsors",
    name: "Clara Live Sponsors",
    path: "/admin/clara-sponsors",
    section: "system-admin",
    adminOnly: true,
    description: "Manage sponsor slot inventory for Clara Live",
    keywords: ["admin", "sponsors", "clara"],
  },
  {
    id: "admin-data-health",
    name: "Data Health",
    path: "/admin/data-health",
    section: "system-admin",
    adminOnly: true,
    description: "Pipeline freshness, fact-table status, ingest cron history",
    keywords: ["admin", "data", "health", "pipeline", "ingest"],
  },
  {
    id: "admin-commentary-facts",
    name: "Commentary Facts",
    path: "/admin/commentary-facts",
    section: "system-admin",
    adminOnly: true,
    description: "Curate the fact set the AI commentary engine draws from",
    keywords: ["admin", "commentary", "AI", "facts"],
  },
  {
    id: "admin-barn-apps",
    name: "Barn Apps",
    path: "/admin/barn-apps",
    section: "system-admin",
    adminOnly: true,
    description: "Every barn app in the registry, with a link to each barn's public splash page",
    highlights: [
      "Grouped: live in the stores, published but not yet live, and preview-only",
      "Opens the page a barn texts its customers — the app running in a phone",
      "Reads the registry directly, so a newly scaffolded barn appears with no extra step",
    ],
    keywords: ["admin", "barn", "apps", "white label", "splash", "tenant", "registry"],
  },
  // ⚠️ NOT the entry above. "Barn Apps" lists the registry; THIS one creates the
  // login a barn owner signs in with at /my-barn/login. Both existed, but only
  // Barn Apps was in the sidebar, so the provisioning screen was reachable only
  // from the TopNav admin dropdown — and was reported as simply missing.
  {
    id: "admin-barns",
    name: "Barn Logins",
    path: "/admin/barns",
    section: "system-admin",
    adminOnly: true,
    description: "Create the owner/manager/staff logins a barn signs in with, and see who already has one",
    highlights: [
      "Picks the barn from live sale data, so a barn is selectable the week it starts selling",
      "Creates the auth user pre-confirmed and links it — no Supabase dashboard, no SQL",
      "Shows which barns already have an account, and every existing member and role",
    ],
    keywords: ["admin", "barn", "login", "owner", "manager", "staff", "my barn", "members", "provision", "account"],
  },
  {
    id: "admin-email-monitor",
    name: "Email Monitor",
    path: "/email-dashboard",
    section: "system-admin",
    adminOnly: true,
    description: "Outbound email delivery + bounce monitoring",
    keywords: ["admin", "email", "monitor"],
  },
];

export function getToolsBySection(sectionId: ToolSectionId): Tool[] {
  return TOOLS.filter((t) => t.section === sectionId);
}

export function getToolByPath(path: string): Tool | undefined {
  return TOOLS.find((t) => t.path === path);
}

export function getSection(sectionId: ToolSectionId): ToolSection | undefined {
  return TOOL_SECTIONS.find((s) => s.id === sectionId);
}
